/**
 * Global Theme Styles
 * Applies design tokens as CSS custom properties for use throughout the app
 */

import { tokens } from './tokens';

export const createGlobalThemeStyles = () => {
  return `
    /* ============================================
       ROOT CSS VARIABLES - DARK THEME (DEFAULT)
       ============================================ */
    :root {
      /* COLORS */
      --color-primary: ${tokens.colors.primary.base};
      --color-primary-dark: ${tokens.colors.primary.dark};
      --color-primary-light: ${tokens.colors.primary.light};
      --color-primary-lighter: ${tokens.colors.primary.lighter};
      --color-primary-faded: ${tokens.colors.primary.faded};

      --color-secondary: ${tokens.colors.secondary.base};
      --color-secondary-dark: ${tokens.colors.secondary.dark};
      --color-secondary-light: ${tokens.colors.secondary.light};
      --color-secondary-lighter: ${tokens.colors.secondary.lighter};
      --color-secondary-faded: ${tokens.colors.secondary.faded};

      --color-success: ${tokens.colors.status.success};
      --color-warning: ${tokens.colors.status.warning};
      --color-error: ${tokens.colors.status.error};
      --color-info: ${tokens.colors.status.info};

      --color-bg: ${tokens.colors.background.base};
      --color-bg-alt: ${tokens.colors.background.alt};
      --color-surface: ${tokens.colors.surface.base};
      --color-surface-raised: ${tokens.colors.surface.raised};
      --color-surface-hover: ${tokens.colors.surface.hover};

      --color-text: ${tokens.colors.text.primary};
      --color-text-secondary: ${tokens.colors.text.secondary};
      --color-text-muted: ${tokens.colors.text.muted};
      --color-text-inverse: ${tokens.colors.text.inverse};

      --color-border-light: ${tokens.colors.border.light};
      --color-border: ${tokens.colors.border.base};
      --color-border-dark: ${tokens.colors.border.dark};

      --color-focus: ${tokens.colors.interactive.focus};
      --color-disabled: ${tokens.colors.interactive.disabled};
      --color-overlay: ${tokens.colors.interactive.overlay};

      /* TYPOGRAPHY */
      --font-primary: ${tokens.typography.fonts.primary};
      --font-mono: ${tokens.typography.fonts.mono};

      --font-size-xs: ${tokens.typography.sizes.xs};
      --font-size-sm: ${tokens.typography.sizes.sm};
      --font-size-base: ${tokens.typography.sizes.base};
      --font-size-lg: ${tokens.typography.sizes.lg};
      --font-size-xl: ${tokens.typography.sizes.xl};
      --font-size-2xl: ${tokens.typography.sizes['2xl']};
      --font-size-3xl: ${tokens.typography.sizes['3xl']};
      --font-size-4xl: ${tokens.typography.sizes['4xl']};

      --font-weight-light: ${tokens.typography.weights.light};
      --font-weight-normal: ${tokens.typography.weights.normal};
      --font-weight-medium: ${tokens.typography.weights.medium};
      --font-weight-semibold: ${tokens.typography.weights.semibold};
      --font-weight-bold: ${tokens.typography.weights.bold};
      --font-weight-extrabold: ${tokens.typography.weights.extrabold};

      --line-height-tight: ${tokens.typography.lineHeights.tight};
      --line-height-normal: ${tokens.typography.lineHeights.normal};
      --line-height-relaxed: ${tokens.typography.lineHeights.relaxed};
      --line-height-loose: ${tokens.typography.lineHeights.loose};

      /* SPACING */
      --space-0: ${tokens.spacing[0]};
      --space-1: ${tokens.spacing[1]};
      --space-2: ${tokens.spacing[2]};
      --space-3: ${tokens.spacing[3]};
      --space-4: ${tokens.spacing[4]};
      --space-6: ${tokens.spacing[6]};
      --space-8: ${tokens.spacing[8]};
      --space-10: ${tokens.spacing[10]};
      --space-12: ${tokens.spacing[12]};
      --space-16: ${tokens.spacing[16]};
      --space-20: ${tokens.spacing[20]};
      --space-24: ${tokens.spacing[24]};

      /* BORDER RADIUS */
      --radius-none: ${tokens.borderRadius.none};
      --radius-sm: ${tokens.borderRadius.sm};
      --radius-base: ${tokens.borderRadius.base};
      --radius-md: ${tokens.borderRadius.md};
      --radius-lg: ${tokens.borderRadius.lg};
      --radius-xl: ${tokens.borderRadius.xl};
      --radius-2xl: ${tokens.borderRadius['2xl']};
      --radius-full: ${tokens.borderRadius.full};

      /* SHADOWS */
      --shadow-none: ${tokens.shadows.none};
      --shadow-sm: ${tokens.shadows.sm};
      --shadow-base: ${tokens.shadows.base};
      --shadow-md: ${tokens.shadows.md};
      --shadow-lg: ${tokens.shadows.lg};
      --shadow-xl: ${tokens.shadows.xl};

      --shadow-elevation-1: ${tokens.shadows.elevation[1]};
      --shadow-elevation-2: ${tokens.shadows.elevation[2]};
      --shadow-elevation-3: ${tokens.shadows.elevation[3]};
      --shadow-elevation-4: ${tokens.shadows.elevation[4]};

      --shadow-hover: ${tokens.shadows.interactive.hover};
      --shadow-active: ${tokens.shadows.interactive.active};
      --shadow-focus: ${tokens.shadows.interactive.focus};

      /* TRANSITIONS */
      --transition-fast: ${tokens.transitions.duration.fast};
      --transition-normal: ${tokens.transitions.duration.normal};
      --transition-slow: ${tokens.transitions.duration.slow};
      --transition-slower: ${tokens.transitions.duration.slower};

      --transition-all: ${tokens.transitions.common.all};
      --transition-bg: ${tokens.transitions.common.background};
      --transition-color: ${tokens.transitions.common.color};
      --transition-opacity: ${tokens.transitions.common.opacity};
      --transition-transform: ${tokens.transitions.common.transform};
      --transition-shadow: ${tokens.transitions.common.shadow};

      /* Z-INDEX */
      --z-hide: ${tokens.zIndex.hide};
      --z-base: ${tokens.zIndex.base};
      --z-dropdown: ${tokens.zIndex.dropdown};
      --z-sticky: ${tokens.zIndex.sticky};
      --z-fixed: ${tokens.zIndex.fixed};
      --z-backdrop: ${tokens.zIndex.backdrop};
      --z-offcanvas: ${tokens.zIndex.offcanvas};
      --z-modal: ${tokens.zIndex.modal};
      --z-popover: ${tokens.zIndex.popover};
      --z-tooltip: ${tokens.zIndex.tooltip};
      --z-notification: ${tokens.zIndex.notification};

      /* SIZES */
      --size-icon-xs: ${tokens.sizes.icon.xs};
      --size-icon-sm: ${tokens.sizes.icon.sm};
      --size-icon-base: ${tokens.sizes.icon.base};
      --size-icon-lg: ${tokens.sizes.icon.lg};
      --size-icon-xl: ${tokens.sizes.icon.xl};
      --size-icon-2xl: ${tokens.sizes.icon['2xl']};

      --component-height-xs: ${tokens.sizes.componentHeight.xs};
      --component-height-sm: ${tokens.sizes.componentHeight.sm};
      --component-height-base: ${tokens.sizes.componentHeight.base};
      --component-height-lg: ${tokens.sizes.componentHeight.lg};
      --component-height-xl: ${tokens.sizes.componentHeight.xl};
    }

    /* ============================================
       LIGHT THEME VARIANT
       ============================================ */
    [data-theme="light"] {
      --color-bg: #ffffff;
      --color-bg-alt: #f8f9fa;
      --color-surface: #f3f4f6;
      --color-surface-raised: #e5e7eb;
      --color-surface-hover: rgba(34, 197, 94, 0.05);

      --color-text: #111827;
      --color-text-secondary: #4b5563;
      --color-text-muted: #6b7280;
      --color-text-inverse: #f8fafc;

      --color-border-light: #d1d5db;
      --color-border: #9ca3af;
      --color-border-dark: #6b7280;
    }

    /* ============================================
       BASE ELEMENT STYLES
       ============================================ */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html {
      width: 100%;
      min-height: 100%;
      font-size: 16px;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      overflow-x: hidden;
    }

    body {
      width: 100%;
      min-width: 0;
      min-height: 100vh;
      min-height: 100dvh;
      font-family: var(--font-primary);
      font-size: var(--font-size-base);
      line-height: var(--line-height-normal);
      color: var(--color-text);
      background-color: var(--color-bg);
      overflow-x: hidden;
      transition: var(--transition-bg);
    }

    #root {
      width: 100%;
      max-width: 100%;
      min-height: 100vh;
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      overflow-x: clip;
    }

    /* ============================================
       TYPOGRAPHY DEFAULTS
       ============================================ */
    h1 {
      font-size: var(--font-size-4xl);
      font-weight: var(--font-weight-bold);
      line-height: var(--line-height-tight);
      letter-spacing: 0;
      margin-bottom: var(--space-4);
    }

    h2 {
      font-size: var(--font-size-3xl);
      font-weight: var(--font-weight-bold);
      line-height: var(--line-height-tight);
      margin-bottom: var(--space-3);
    }

    h3 {
      font-size: var(--font-size-2xl);
      font-weight: var(--font-weight-semibold);
      line-height: var(--line-height-tight);
      margin-bottom: var(--space-2);
    }

    h4 {
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-semibold);
      margin-bottom: var(--space-2);
    }

    h5 {
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-semibold);
      margin-bottom: var(--space-1);
    }

    h6 {
      font-size: var(--font-size-base);
      font-weight: var(--font-weight-semibold);
    }

    p {
      margin-bottom: var(--space-4);
      line-height: var(--line-height-relaxed);
    }

    a {
      color: var(--color-primary);
      text-decoration: none;
      transition: var(--transition-color);
    }

    a:hover {
      color: var(--color-primary-light);
      text-decoration: underline;
    }

    a:focus {
      outline: none;
      border-radius: var(--radius-sm);
      box-shadow: var(--shadow-focus);
    }

    /* ============================================
       FORM ELEMENTS
       ============================================ */
    input,
    select,
    textarea {
      font-family: var(--font-primary);
      font-size: inherit;
      color: var(--color-text);
      background-color: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-base);
      padding: var(--space-2) var(--space-3);
      transition: var(--transition-all);
    }

    input:focus,
    select:focus,
    textarea:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: var(--shadow-focus);
    }

    input:disabled,
    select:disabled,
    textarea:disabled {
      background-color: var(--color-surface-raised);
      color: var(--color-text-muted);
      cursor: not-allowed;
      opacity: 0.6;
    }

    /* ============================================
       SCROLLBAR STYLING
       ============================================ */
    ::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    ::-webkit-scrollbar-track {
      background: var(--color-surface);
    }

    ::-webkit-scrollbar-thumb {
      background: var(--color-border-dark);
      border-radius: var(--radius-full);
    }

    ::-webkit-scrollbar-thumb:hover {
      background: var(--color-text-muted);
    }

    /* ============================================
       UTILITY CLASSES
       ============================================ */
    .text-primary {
      color: var(--color-text);
    }

    .text-secondary {
      color: var(--color-text-secondary);
    }

    .text-muted {
      color: var(--color-text-muted);
    }

    .bg-primary {
      background-color: var(--color-primary);
    }

    .bg-surface {
      background-color: var(--color-surface);
    }

    .border-base {
      border: 1px solid var(--color-border);
    }

    .rounded-lg {
      border-radius: var(--radius-lg);
    }

    .shadow-md {
      box-shadow: var(--shadow-md);
    }

    .transition-all {
      transition: var(--transition-all);
    }

    /* ============================================
       SELECTION STYLING
       ============================================ */
    ::selection {
      background-color: var(--color-primary);
      color: var(--color-bg);
    }

    ::-moz-selection {
      background-color: var(--color-primary);
      color: var(--color-bg);
    }
  `;
};

export default createGlobalThemeStyles;
